import { CouponBase } from "./coupon-base";
import { FirstMatchGroupItemTriggerItemsQualifier } from "../coupon-qualifiers/trigger-items/first-match-group-item-trigger-items";
import { Util } from "../../../util/util";
// Setting 22 (Half & Half Pricing Option)
const HALF_HALF_PRICING_HIGHEST = 1;
const HALF_HALF_PRICING_AVERAGE = 2;
/**
 * "Upgrade" coupon (type 40) — "Buy {Size} for the Price of {Size}", e.g. a large pizza for the
 * price of a medium. Unlike the fixed-price types there is no manually entered price point: the
 * price comes from re-pricing the qualified item at the upgrade's PriceSizeId, using the host's
 * menu pricing for the cart's price group (ICouponDeps.getItemPrice).
 *
 * The item is re-priced whole — the menu item AND all of its modifiers — so the customer still pays
 * for every topping, just at the upgraded-to size's modifier rates. The topping count on the coupon
 * item is a qualification rule (with the Must Match flag) and has no say in the discount amount.
 */
export class UpgradeCoupon extends CouponBase {
    constructor(appType, sourceData, dependencies, baseData, identity) {
        super(appType, sourceData, dependencies, baseData, identity);
        this._preQualifiers.push(new FirstMatchGroupItemTriggerItemsQualifier(this._data));
    }
    getDiscountItems(cart) {
        return this._getDiscountItemsFromTriggers(cart);
    }
    calculateDiscount(cart) {
        const discountItems = this.getDiscountItems(cart);
        const totalAmounts = this._getTotalCouponItemsPrices(discountItems);
        let discountAmount = 0;
        const upgradedPrice = this._getUpgradedPrice(cart, discountItems);
        if (upgradedPrice > 0) {
            discountAmount = Util.Float2(totalAmounts.orderItemPrice - upgradedPrice);
        }
        discountAmount = Math.max(0, this._adjustDiscountAmount(cart, discountAmount));
        if (this._coupon.isBeforeTax) {
            this.getBeforeTaxDistributor().distribute({
                cart,
                discountItems,
                totalDiscountedItemsPrice: totalAmounts.orderItemPrice,
                computedDiscountToAllocate: discountAmount,
            });
        }
        else {
            this.getAfterTaxDistributor().distribute({
                cart,
                discountItems,
                totalDiscountedItemsPrice: totalAmounts.orderItemPrice,
                computedDiscountToAllocate: discountAmount
            });
        }
        return [discountAmount, discountItems];
    }
    /**
     * Total price the qualified items should be charged at once upgraded.
     *
     * Whole items: each qualified item is priced at its own upgraded price and those prices are
     * summed. For identical items that is the same as charging the upgrade price per item (Bug
     * 72113: two larges at the medium's $15 leave a $30 subtotal); summing additionally prices them
     * correctly when they carry different modifiers. AOLO3 (coupons.ts
     * `_getDiscountAmountForUpgradeCoupon`) and POS (coupon-logic.ts `CouponTypeList.Upgrade`)
     * instead multiply the first qualified item's price by the qualified-item count.
     *
     * Half & half: the halves together make up a single item, so the upgrade price for the item as
     * a whole is the total — it is NOT multiplied by the number of halves. That multiplier is what
     * drives the half & half discount negative in the host apps today, so an Upgrade coupon never
     * discounts a half & half (Bug 73510); the multiplier itself is right for a QUANTITY of whole
     * items, and two halves are one pizza rather than two.
     */
    _getUpgradedPrice(cart, discountItems) {
        if (discountItems.length === 0) {
            return 0;
        }
        const isHalfHalf = discountItems.length > 1 && (discountItems[0].halfCount ?? 1) > 1;
        if (isHalfHalf) {
            return this._getHalfHalfUpgradePrice(cart, discountItems);
        }
        let totalPrice = 0;
        for (const discountItem of discountItems) {
            const cartItem = cart.items[discountItem.itemArrayIndex];
            if (!cartItem) {
                return 0;
            }
            const price = this._priceItemAtUpgradeSize(cart, cartItem);
            if (price <= 0) {
                return 0;
            }
            totalPrice += price;
        }
        return Util.Float2(totalPrice);
    }
    /**
     * Half & half price for the upgraded item, driven by Setting 22: "Highest Price" charges the
     * dearest half's upgraded price, "Average Price" splits the difference across the halves.
     * A half whose size does not match a configured Buy Size contributes nothing.
     *
     * This is the whole of half & half within this coupon type's scope — that the item is priced as
     * one pizza rather than once per half. How Setting 22 chooses between the halves, how the
     * coupon's topping count is shared or split across them, and what a priced modifier sitting on
     * only one half costs all belong to US 82541 (Half & Half Qualification and Coupon Pricing
     * Logic), which lists this story as a precondition. Nothing here accepts or rejects that
     * behavior.
     */
    _getHalfHalfUpgradePrice(cart, discountItems) {
        let highestPrice = 0;
        let totalPrice = 0;
        for (const discountItem of discountItems) {
            const cartItem = cart.items[discountItem.itemArrayIndex];
            if (!cartItem) {
                continue;
            }
            const price = this._priceItemAtUpgradeSize(cart, cartItem);
            if (price <= 0) {
                continue;
            }
            if (price > highestPrice) {
                highestPrice = price;
            }
            totalPrice += price;
        }
        switch (this._data.settings.HHP) {
            case HALF_HALF_PRICING_HIGHEST:
                return highestPrice;
            case HALF_HALF_PRICING_AVERAGE:
                return Util.Float2(totalPrice / discountItems.length);
            default:
                return 0;
        }
    }
    /**
     * Re-prices a cart item at the size it upgrades to, carrying the menu item and all of its
     * modifiers, against the order's current price group. Returns 0 when the item's size is not a
     * configured Buy Size, or the host supplied no pricer.
     *
     * The host returns a whole-item price, so halves are priced as if they covered the whole item —
     * the caller decides how to combine them.
     */
    _priceItemAtUpgradeSize(cart, cartItem) {
        const getItemPrice = this._dependencies?.getItemPrice;
        if (!getItemPrice) {
            return 0;
        }
        for (const upgrade of this._coupon.upgrades ?? []) {
            if (cartItem.sizeId !== upgrade.buySizeId) {
                continue;
            }
            const price = Math.abs(getItemPrice({
                itemId: cartItem.id,
                sizeId: upgrade.priceSizeId,
                modifiers: cartItem.modifiers,
                priceGroupId: cart.priceGroupId
            }));
            if (price > 0) {
                return Util.Float2(price);
            }
        }
        return 0;
    }
}
//# sourceMappingURL=upgrade.js.map